import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { ChangeAccessLevelConfirmDialog } from "@components/project/change-access-level-confirm-dialog";
import { AccessLevelDropdown } from "@components/access-level/access-level-dropdown";
import { AccessLevelTooltip } from "@components/access-level/access-level-tooltip";
import { AccessLevelDisplayNames } from "@components/access-level/access-level-utils";
import { blue, neutral } from "@flat-ui";
import { useOnAccessLevelChange } from "@hooks/projects/use-on-access-level-change";
import { useMediaQueryList } from "@hooks/use-media-query";
import { FormControl } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DEFAULT_INPUT_FONT_SIZE } from "@styles/common-styles";
import { CSSProperties, useState } from "react";
import { AccessLevelProps } from "@components/access-level/access-level-types";

/** Component width */
const ACCESS_LEVEL_COMPONENT_WIDTH: CSSProperties["width"] = "225px";

/**
 * Access level selection component
 * If the user has permission to change the access level it renders a custom dropdown,
 * otherwise it shows the access level as readonly text.
 */
export function AccessLevel({
  type,
  isAccessLevelEditable,
  entity,
  location,
  sx,
}: AccessLevelProps): JSX.Element {
  const { isScreenXs } = useMediaQueryList();
  const { onAccessLevelChange } = useOnAccessLevelChange(type);

  const [selectedAccessLevel, setSelectedAccessLevel] = useState<
    FaroCoreApiTypes.ProjectAccessLevel | undefined
  >();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function handleSelectAccessLevel(
    accessLevel: FaroCoreApiTypes.ProjectAccessLevel
  ): void {
    setSelectedAccessLevel(accessLevel);
    setIsDialogOpen(true);
  }

  async function handleConfirm(): Promise<void> {
    if (!selectedAccessLevel) {
      return;
    }

    setIsLoading(true);
    try {
      await onAccessLevelChange(selectedAccessLevel, location, entity.id);
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
    }
  }

  return (
    <FormControl
      sx={{
        width: "100%",
        ...sx,
      }}
    >
      <AccessLevelTooltip type={type} />
      {isAccessLevelEditable ? (
        <>
          <FaroTextMenuButton
            buttonTitle={AccessLevelDisplayNames[entity.accessLevel]}
            buttonSx={() => {
              return {
                color: neutral[800],
                fontSize: DEFAULT_INPUT_FONT_SIZE,
                // For extra-small screens show the component in full width
                width: isScreenXs ? "100%" : ACCESS_LEVEL_COMPONENT_WIDTH,
                textOverflow: "ellipsis",
                justifyContent: "space-between",

                // Not focused
                "&:not(&:focus)": {
                  // Hide the underline and icon by default
                  "&:before": {
                    border: "none",
                  },
                  "& .MuiButton-icon": {
                    display: "none",
                  },

                  // Hover style. Show underline and icon
                  "&:hover": {
                    boxShadow: `0px 1px 0px ${neutral[400]}`,

                    "& .MuiButton-icon": {
                      display: "block",
                    },
                  },
                },

                // Focused
                "&:focus": {
                  borderBottom: "2px solid",
                  borderBottomColor: blue[500],
                },
              };
            }}
          >
            <AccessLevelDropdown
              type={type}
              currentAccessLevel={entity.accessLevel}
              onSelectAccessLevel={handleSelectAccessLevel}
            />
          </FaroTextMenuButton>

          {selectedAccessLevel && (
            <ChangeAccessLevelConfirmDialog
              type={type}
              accessLevel={selectedAccessLevel}
              isOpen={isDialogOpen}
              isLoading={isLoading}
              onConfirm={() => void handleConfirm()}
              onClose={() => setIsDialogOpen(false)}
            />
          )}
        </>
      ) : (
        <FaroTextField
          initialValue={AccessLevelDisplayNames[entity.accessLevel]}
          sx={{
            width: ACCESS_LEVEL_COMPONENT_WIDTH,
          }}
        />
      )}
    </FormControl>
  );
}
